import React, { useState } from 'react';
import { FaWhatsapp } from 'react-icons/fa';
import fields from '../signupFields';
import { PHONE, PHONE_HREF, WHATSAPP_HREF, SIGNUP_ENDPOINT } from '../site';
import { track } from '../analytics';

const inputClass =
  'w-full rounded-lg border border-navy/25 px-4 py-2.5 text-gray-900 outline-none transition focus:border-navy focus:ring-2 focus:ring-navy/20';

const Required = () => <span className="text-red-600"> *</span>;

const Field = ({ field, value, onChange, error }) => {
  const { name, label, type, required, options, hint } = field;

  if (type === 'checkboxes') {
    const selected = value || [];
    const toggle = (option) =>
      onChange(
        name,
        selected.includes(option)
          ? selected.filter((o) => o !== option)
          : [...selected, option]
      );

    return (
      <fieldset>
        <legend className="font-medium text-navy">
          {label}
          {required && <Required />}
        </legend>
        {hint && <p className="mb-2 text-sm text-gray-500">{hint}</p>}
        <div className="grid gap-2 sm:grid-cols-2">
          {options.map((option) => (
            <label
              key={option}
              className="flex cursor-pointer items-center gap-2.5 rounded-lg border border-navy/20 px-3 py-2.5 transition hover:border-navy hover:bg-navy/5"
            >
              <input
                type="checkbox"
                checked={selected.includes(option)}
                onChange={() => toggle(option)}
                className="h-4 w-4 accent-navy"
              />
              <span className="text-gray-800">{option}</span>
            </label>
          ))}
        </div>
        {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
      </fieldset>
    );
  }

  const shared = {
    id: name,
    name,
    value: value || '',
    required,
    onChange: (e) => onChange(name, e.target.value),
    className: inputClass,
  };

  return (
    <div>
      <label htmlFor={name} className="block font-medium text-navy">
        {label}
        {required && <Required />}
      </label>
      {hint && <p className="mb-1.5 text-sm text-gray-500">{hint}</p>}
      {!hint && <div className="mb-1.5" />}
      {type === 'textarea' ? <textarea {...shared} rows={3} /> : <input {...shared} type={type} />}
    </div>
  );
};

const SignUpForm = () => {
  const [values, setValues] = useState({});
  const [status, setStatus] = useState('idle');
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState('');

  const onChange = (name, value) => {
    setValues((v) => ({ ...v, [name]: value }));
    setFieldErrors((e) => ({ ...e, [name]: '' }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    // Checkbox groups aren't covered by the browser's own required check.
    const missing = {};
    fields.forEach((f) => {
      if (f.type === 'checkboxes' && f.required && !(values[f.name] || []).length) {
        missing[f.name] = 'Please choose at least one.';
      }
    });
    if (Object.keys(missing).length) {
      setFieldErrors(missing);
      return;
    }

    setStatus('submitting');
    setError('');
    try {
      const payload = { submittedAt: new Date().toISOString() };
      fields.forEach((f) => {
        const v = values[f.name];
        payload[f.name] = Array.isArray(v) ? v.join(', ') : v || '';
      });
      if (values.company) payload.company = values.company;

      // URLSearchParams keeps this a "simple" request, so the browser skips the
      // CORS preflight that Apps Script can't answer.
      const res = await fetch(SIGNUP_ENDPOINT, {
        method: 'POST',
        body: new URLSearchParams(payload),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.error || 'Request failed');

      // Only after the row is confirmed written — never on a hopeful guess.
      track('CompleteRegistration', {
        userData: {
          fullName: values.fullName,
          phone: values.phone,
          email: values.email,
        },
        custom: { content_name: payload.classes },
      });

      setStatus('success');
    } catch (err) {
      setStatus('error');
      setError('We could not send that. Please try again, or message us on WhatsApp.');
    }
  };

  return (
    <div className="overflow-hidden rounded-xl border-2 border-navy bg-white shadow-xl">
      <div className="bg-navy px-6 py-5">
        <h2 className="text-2xl font-bold text-white">Sign up for classes</h2>
        <p className="mt-1 text-white/80">Register your spot for the next session.</p>
      </div>

      {!SIGNUP_ENDPOINT ? (
        <div className="px-6 py-10 text-center">
          <p className="text-gray-700">
            Our online sign-up form is on the way. In the meantime, call or message
            us to register.
          </p>
          <a
            href={PHONE_HREF}
            onClick={() => track('Contact')}
            className="mt-5 inline-block rounded-lg bg-navy px-6 py-3 font-medium text-white transition hover:bg-navy-light"
          >
            {PHONE}
          </a>
        </div>
      ) : status === 'success' ? (
        <div className="px-6 py-12 text-center">
          <p className="text-xl font-semibold text-navy">Thanks — you&rsquo;re signed up.</p>
          <p className="mt-2 text-gray-700">
            We&rsquo;ll be in touch on WhatsApp shortly with the details.
          </p>
          <a
            href={WHATSAPP_HREF}
            target="_blank"
            rel="noreferrer"
            onClick={() => track('Contact')}
            className="mt-6 inline-flex items-center gap-2 rounded-lg bg-navy px-6 py-3 font-medium text-white transition hover:bg-navy-light"
          >
            <FaWhatsapp aria-hidden="true" /> Message us on WhatsApp
          </a>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="flex flex-col gap-5 px-6 py-7">
          {fields.map((field) => (
            <Field
              key={field.name}
              field={field}
              value={values[field.name]}
              onChange={onChange}
              error={fieldErrors[field.name]}
            />
          ))}

          {/* Spam trap — hidden from people, tempting to bots. */}
          <input
            type="text"
            name="company"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            className="absolute left-[-9999px]"
            onChange={(e) => onChange('company', e.target.value)}
          />

          {status === 'error' && (
            <p className="rounded-lg bg-red-50 px-4 py-3 text-red-700">{error}</p>
          )}

          <button
            type="submit"
            disabled={status === 'submitting'}
            className="rounded-lg bg-navy px-6 py-3.5 text-lg font-medium text-white transition hover:bg-navy-light disabled:opacity-60"
          >
            {status === 'submitting' ? 'Sending…' : 'Sign up'}
          </button>

          <p className="text-center text-sm text-gray-500">
            Rather talk to us?{' '}
            <a
              href={WHATSAPP_HREF}
              target="_blank"
              rel="noreferrer"
              onClick={() => track('Contact')}
              className="text-navy underline"
            >
              WhatsApp {PHONE}
            </a>
          </p>
        </form>
      )}
    </div>
  );
};

export default SignUpForm;
